import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AdminLayout from "../layouts/AdminLayout";
import { hasPermission } from "../utils/permissions";

function EditProduct() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    title: "",
    price: "",
    description: "",
    category: "clothes",
    subcategory: "men",
    size: [],
    color: "",
    offer: "",
    tags: [],
    image: null,
  });
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [hasAccess, setHasAccess] = useState(true); // 👈 Local guard

  useEffect(() => {
    // ✅ Check permission once component has loaded
    const access = hasPermission("products", "edit");
    setHasAccess(access);

    if (!access || !id) {
      setLoading(false);
      setError(!access ? "You do not have permission to edit products." : "Missing product ID.");
      return;
    }

    fetch(`https://kolibusiness.com/backend/get-product.php?id=${id}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.error) {
          setError(data.error);
        } else {
          setForm({
            title: data.title,
            price: data.price,
            description: data.description,
            category: data.category || "clothes",
            subcategory: data.subcategory || "men",
            size: data.size ? data.size.split(",") : [],
            color: data.color || "",
            offer: data.offer || "",
            tags: data.tags ? data.tags.split(",") : [],
            image: null,
          });
          setPreview(`https://kolibusiness.com/backend/uploads/${data.image}`);
        }
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load product data. Please try again later.");
        setLoading(false);
      });
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === "file") {
      setForm({ ...form, image: files[0] });
      setPreview(URL.createObjectURL(files[0]));
    } else if (type === "checkbox") {
      if (name === "size") {
        const sizes = checked ? [...form.size, value] : form.size.filter((s) => s !== value);
        setForm({ ...form, size: sizes });
      } else if (name === "tags") {
        const tags = checked ? [...form.tags, value] : form.tags.filter((t) => t !== value);
        setForm({ ...form, tags });
      }
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("id", id);
    formData.append("title", form.title);
    formData.append("price", form.price);
    formData.append("description", form.description);
    formData.append("category", form.category);
    formData.append("subcategory", form.subcategory);
    formData.append("size", form.size.join(","));
    formData.append("color", form.color);
    formData.append("offer", form.offer);
    formData.append("tags", form.tags.join(","));
    if (form.image) {
      formData.append("image", form.image);
    }

    try {
      const res = await fetch("https://kolibusiness.com/backend/update-product.php", {
        method: "POST",
        body: formData,
      });

      const result = await res.json();
      if (result.success) {
        alert("✅ Product updated successfully!");
        navigate("/admin/products");
      } else {
        alert("Update failed: " + (result.message || "Unknown error"));
      }
    } catch (err) {
      alert("Network error. Try again later.");
    }
  };

  if (loading) return <AdminLayout><p>Loading product...</p></AdminLayout>;

  if (!hasAccess || error) {
    return (
      <AdminLayout>
        <p style={{ color: "red", padding: "20px", fontWeight: "bold" }}>⚠️ {error}</p>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <h2>Edit Product</h2>
      <form onSubmit={handleSubmit} style={styles.form}>
        <h3>📝 Basic Info</h3>
        <input name="title" value={form.title} onChange={handleChange} placeholder="Product Name" required />
        <input name="price" value={form.price} onChange={handleChange} placeholder="Price" required />
        <textarea name="description" value={form.description} onChange={handleChange} placeholder="Description" />

        <h3>📦 Categorization</h3>
        <select name="category" value={form.category} onChange={handleChange}>
          <option value="clothes">Clothes</option>
          <option value="electronics">Electronics</option>
          <option value="furniture">Furniture</option>
        </select>
        <select name="subcategory" value={form.subcategory} onChange={handleChange}>
          <option value="men">Men</option>
          <option value="women">Women</option>
          <option value="kids">Kids</option>
        </select>

        <h3>📐 Sizes</h3>
        <div style={styles.checkboxGroup}>
          {["S", "M", "L", "XL", "XXL"].map((size) => (
            <label key={size}>
              <input type="checkbox" name="size" value={size} checked={form.size.includes(size)} onChange={handleChange} /> {size}
            </label>
          ))}
        </div>

        <h3>🎨 Color & Offer</h3>
        <input name="color" value={form.color} onChange={handleChange} placeholder="Colors (comma-separated)" />
        <input name="offer" value={form.offer} onChange={handleChange} placeholder="Offer or Discount (e.g. 20%)" />

        <h3>🔖 Tags</h3>
        <div style={styles.checkboxGroup}>
          {["hot", "similar", "new"].map((tag) => (
            <label key={tag}>
              <input type="checkbox" name="tags" value={tag} checked={form.tags.includes(tag)} onChange={handleChange} /> {tag}
            </label>
          ))}
        </div>

        <h3>🖼️ Product Image</h3>
        {preview && <img src={preview} alt="Preview" style={{ width: "100px" }} />}
        <input type="file" name="image" onChange={handleChange} />

        <button type="submit" style={styles.submit}>Update Product</button>
      </form>
    </AdminLayout>
  );
}

const styles = {
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    maxWidth: "600px",
    padding: "20px",
    background: "#fff",
    borderRadius: "8px",
    boxShadow: "0 0 10px rgba(0,0,0,0.1)",
    marginBottom: "40px"
  },
  checkboxGroup: {
    display: "flex",
    gap: "10px",
    flexWrap: "wrap"
  },
  submit: {
    background: "#0984e3",
    color: "#fff",
    padding: "12px",
    fontSize: "16px",
    borderRadius: "6px",
    fontWeight: "bold",
    border: "none",
    cursor: "pointer"
  }
};

export default EditProduct;
